import React from 'react';
import { computed, action, observable, entries, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import SearchableComboBox from 'src/components/base/SearchableComboBox';

import PushFlowstep from 'src/components/Flowsteps/PushData/PushFlowstep';
import Step from 'src/components/Flowsteps/base/Step';

import * as Mapping from 'src/components/Flowsteps/PushData/Mapping';

import { Type, generateFlowstepName } from 'shared/utils/flowstep';

import * as QA from 'shared/utils/queryAnalysis';


const Const = {
  SupportedObjects: [
    QA.KnownObjects.Account, 
    QA.KnownObjects.Contact, 
    QA.KnownObjects.Lead, 
    QA.KnownObjects.Individual
  ],
};



@inject('salesforceStore', 'dataStore', 'officeStore', 'userStore')
export class MergeObjects extends PushFlowstep {
  @observable showMappingPage = false;
  @observable mappingManager = null;

  constructor(props) {
    super(props, { type: Type.MergeObjects, types: [Type.MergeObjects] });
    makeObservable(this);

    this.options = {
      ...this.options,
      merge: {
        columnIndexes: {
          masterId: null,
          victimId: null,
        },
      },
    };

    this.mappingManager = new Mapping.Manager(
      { salesforceStore: this.props.salesforceStore },
      {
        onSetDirty: this.setDirty, objectFieldsToIgnore: [QA.KnownFields.Id]
      });

    this.mappingManager.setType(this.type);
    this.mappingManager.setObjectName(Const.SupportedObjects[0]);

    const allReactions = [...super.setupBaseReactions()];
    this.cleanReactions = () => allReactions.forEach(dispose => dispose());
  }

  @computed get _generatedName_() {
    const { worksheetName, targetCell: target } = this.selectionTarget || {};
    return generateFlowstepName({ type: this.type, worksheetName, target, sfObjectName: this.mappingOptions.objectName });
  }

  @computed get columnErrors() {
    return this.comboboxes.reduce((out, x) => {
      const { id } = x;
      const key = this.options.merge.columnIndexes[id];
      out[id] = key === null || key > this.excelHeaders.length - 1
        ? 'Must be mapped to some column'
        : '';
      return out;
    }, {});
  }

  @computed get areIndexesValid() {
    return Object.values(this.columnErrors).every(x => !x);
  }

  @computed get _isValid_() {
    return this.excelHeaders.length && this.areIndexesValid && this.mappingManager?.isValid && super.areAllOptionsValid;
  }

  @computed get objectOptions() {
    return (this.props ? this.props.officeStore.objects : [])
      .filter(x => Const.SupportedObjects.includes(x.name))
      .map(x => ({ key: x.name, text: x.name }));
  }

  @computed get excelHeaderOptions() {
    return this.excelHeaders
      .map((x, index) => ({ key: index, text: x, index }));
  }

  @computed get canOpenMappingPage() {
    return this.excelHeaders.length && this.mappingOptions.objectName;
  }

  @computed get mappingOptions() {
    return this.mappingManager?.mapping || {};
  }
  
  @computed get comboboxes() {
    return [
      { label: 'Master IDs column', id: 'masterId' },
      { label: 'Victim IDs column', id: 'victimId' },
    ];
  }

  @computed get excelHeadersToIgnore() {
    return entries(this.options.merge.columnIndexes).reduce((out, [columnName, index]) => {
      index !== null && out.push(index);
      return out;
    }, []);
  }


  async _componentDidMountStart_(props) {
    await super._componentDidMountStart_(props);
    return this.props.officeStore.fetchObjects();
  }

  async _componentDidMountEnd_(task) {
    const objects = await task; 
    
    const { objectName } = this.options.merge;
    if (objectName && !objects.find(x => x.name === objectName)) {
      if (this.flowstep) {
        this.officeStore.requestStore.raise(`Object '${objectName}' doesn't exist any more. Select another object and reapply mapping`);
      }

      this.handleObjectChange('');
    }
  }

  @action _setSelectionTarget_(selectionTarget) {
    super._setSelectionTarget_(selectionTarget);
    this.mappingManager.setExcelHeaders(this.excelHeaders);

    // this.similarMapping = null;
    // this.mapping = null;
    // this.mappingOptions.columnNameForResultIds = '';
  }

  async _tryLoadFlowstepOfDifferentType_(allWithSameTarget, allSimilarTypesWithSameTarget) {
    // let soql = null;
    // if (soql = allWithSameTarget.find(x => x.type === Type.Soql)) {
    //   // console.log('Loading similar soql flowstep: ', soql);
    //   // this.mappingOptions.sfObjectName = soql.options.object?.name ?? '';
    //   this.mappingManager.setObjectName(soql.options.object?.name ?? '');
    //   return;
    // }
  }

  async _loadFlowstep_(flowstep, isExact = true) {
    const { merge } = flowstep.options;
    const { mapping, columnIndexes } = merge;
    
    this.options = { ...this.options, merge: { columnIndexes }};
    this.updateHeadersToIgnore();

    // console.log('MergeObjects::_loadFlowstep_', isExact, mapping);
    this.mappingManager.setMapping(mapping, { isExact });
    isExact && super.loadFlowstep(flowstep, isExact);
  }

  async _makeFlowstep_(makeCopy) {
    const flowstep = super.makeFlowstep(makeCopy);
    const mapping = this.mappingManager.makeMapping();

    // console.log('MergeObjects::_makeFlowstep_: mapping: ', mapping);
    const { columnIndexes } = this.options.merge;
    Object.assign(flowstep.options, { merge: { mapping, columnIndexes }});
    return flowstep;
  }


  _renderCustomSteps_({ getStepTitle }) {
    return (
      <>
        <Step title={getStepTitle('Select object')}>
          <SearchableComboBox
            placeholder="Select Object"
            selectedKey={this.mappingOptions.objectName}
            onChange={(_, item) => this.handleObjectChange(item.key)}
            disabled={!this.objectOptions.length || this._readOnly_}
            options={this.objectOptions}
          />
        </Step>

        <Step title={getStepTitle('Select column with IDs')}>
          { this.comboboxes.map(({ label, id }) => {
            const key = this.options.merge.columnIndexes[id];
            const message = this.columnErrors[id];
            return (
              <SearchableComboBox
                key={id}
                label={label}
                placeholder="Select column"
                selectedKey={key}
                onChange={(_, item) => this.handleColumnIndexOptionChange(id, item.index)}
                options={this.excelHeaderOptions}
                disabled={!this.excelHeaderOptions.length || this._readOnly_}
                errorMessage={message}
              />
              );
            }
          )}
        </Step>

        <Step title={ getStepTitle('Set up mappings (optional)') } >
          <Mapping.Button
            mappingManager={this.mappingManager}
            label={'Mappings'}
            isDisabled={!this.canOpenMappingPage}
            isEditingDisabled={this._readOnly_}
          />
        </Step>
      </>
    );
  }

  @action handleColumnIndexOptionChange = (id, value) => {
    this.options.merge.columnIndexes[id] = value;
    this.updateHeadersToIgnore();
    this.setDirty();
  }

  @action handleObjectChange = (objectName) => {
    this.mappingManager.setObjectName(objectName);
  }

  updateHeadersToIgnore() {
    this.mappingManager.setExcelHeadersToIgnore(this.excelHeadersToIgnore);
  }
}
